<template>
    <div class="flex w-full items-center gap-1" @dragstart="dragStart" @dragend="dragEnd">
        <div v-if="!props.simple" class="resource-dragging-hide shrink-0">
            <vx-button color="transparent" size="sm" type="button" class="cursor-move! resource-handle">
                <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
            </vx-button>
        </div>
        <div
            class="resource-item bg-white w-full rounded-2xl shadow border-solid border-t-0 border-l border-r border-b border-white items-center inline-flex"
        >
            <div
                class="resource-icon w-40 self-stretch pl-5 pr-8 py-2 rounded-tl-2xl rounded-bl-2xl border border-solid border-white/50 items-center gap-4 flex"
                :class="options.color"
            >
                <div class="items-center gap-2 flex">
                    <div class="w-5 h-5 justify-center items-center flex">
                        <font-awesome-icon fixedWidth :icon="options.icon"></font-awesome-icon>
                    </div>
                    <div class="resource-dragging-hide text-slate-800 text-sm font-extrabold leading-tight capitalize">
                        {{ props.resource.type }}
                    </div>
                </div>
            </div>
            <div class="resource-dragging-hide grow shrink basis-0 h-16 pl-5 pr-3 py-3 items-center gap-4 flex">
                <div class="grow shrink basis-0 text-slate-900 flex items-center">
                    <div class="line-clamp-2">
                        <template v-if="data">
                            {{ data.name }}
                        </template>
                        <template v-else>
                            <vx-icon-loading spin></vx-icon-loading>
                        </template>
                    </div>
                    <resource-details-popover
                        v-if="['campaign', 'automation'].includes(props.resource.type) && data"
                        class="ml-1"
                        :modelValue="{
                            id: data.id,
                            name: data.name,
                            resource_type: props.resource.type,
                        }"
                        :teamId="teamId"
                    ></resource-details-popover>
                    <segment-description-popover
                        v-else-if="'segment' === props.resource.type && data"
                        :modelValue="data"
                        class="ml-1"
                    ></segment-description-popover>
                </div>
                <div v-if="props.simple">
                    <font-awesome-icon widthAuto :icon="faToggleLargeOn"></font-awesome-icon>
                </div>
                <div v-if="!props.simple" class="items-center gap-2 flex">
                    <template v-if="data">
                        <template v-if="props.resource.type === 'automation'">
                            <div>
                                <font-awesome-icon
                                    widthAuto
                                    :icon="!publishedAutomatedActions ? faToggleLargeOff : faToggleLargeOn"
                                ></font-awesome-icon>
                            </div>
                            <div class="text-slate-800 text-sm leading-tight">
                                {{ publishedAutomatedActions ? 'Active' : 'Inactive' }}
                                <vx-badge :borders="false" color="slate" size="xs" class="font-extrabold!">
                                    {{ publishedAutomatedActions }}/{{ totalAutomatedActions }}
                                </vx-badge>
                            </div>
                        </template>
                        <template v-else-if="props.resource.type === 'campaign'">
                            <div>
                                <font-awesome-icon
                                    widthAuto
                                    :icon="
                                        ['draft', 'paused', 'archived'].includes(data.status)
                                            ? faToggleLargeOff
                                            : faToggleLargeOn
                                    "
                                ></font-awesome-icon>
                            </div>
                            <div class="text-slate-800 text-sm leading-tight">
                                {{ CAMPAIGN_STATUSES[data.status]?.label }}
                            </div>
                        </template>
                        <template v-if="props.resource.type === 'segment'">
                            <div class="text-slate-800 text-sm leading-tight">
                                <font-awesome-icon fixedWidth :icon="data.type === 'static' ? faMapPin : faLoader" />
                                {{ SegmentTypeToLabel[data.type] }}
                                <vx-icon-loading
                                    v-if="segmentContactsCount[props.resource.id] == null"
                                    spin
                                ></vx-icon-loading>
                                <template v-else>
                                    {{ vxFormatCount(segmentContactsCount[props.resource.id]) }}
                                </template>
                            </div>
                        </template>
                    </template>
                </div>
                <div v-if="!props.simple" class="items-center gap-2 flex">
                    <vx-dropdown>
                        <vx-button color="muted" outline size="sm">
                            <font-awesome-icon widthAuto :icon="faEllipsis"></font-awesome-icon>
                        </vx-button>
                        <template v-slot:items>
                            <template v-if="props.resource.type === 'automation' && totalAutomatedActions">
                                <vx-dropdown-item
                                    v-if="publishedAutomatedActions !== totalAutomatedActions"
                                    data-test="update-actions-status-publish"
                                    @click.prevent="updateActionsStatus('published')"
                                >
                                    Turn On All Actions
                                    <font-awesome-icon widthAuto :icon="faPlay"></font-awesome-icon>
                                </vx-dropdown-item>
                                <vx-dropdown-item
                                    v-if="publishedAutomatedActions !== 0"
                                    data-test="update-actions-status-draft"
                                    @click.prevent="updateActionsStatus('draft')"
                                >
                                    Turn Off All Actions
                                    <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                                </vx-dropdown-item>
                            </template>
                            <template v-else-if="props.resource.type === 'campaign' && data">
                                <campaign-card-actions
                                    v-model="data"
                                    @launch="campaignLaunch = true"
                                    dropdown-item
                                ></campaign-card-actions>
                            </template>
                            <template v-else-if="props.resource.type === 'segment'">
                                <vx-dropdown-item :href="`/contacts/segments/${props.resource.id}/contacts`">
                                    View Contacts
                                    <font-awesome-icon widthAuto :icon="faAddressBook"></font-awesome-icon>
                                </vx-dropdown-item>
                                <segment-row-export :segmentId="props.resource.id"></segment-row-export>
                            </template>

                            <vx-dropdown-item @click="emit('remove')">
                                Remove from Flow
                                <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                    <vx-button :href="options.edit" color="muted" outline size="sm">
                        Edit
                        <font-awesome-icon widthAuto :icon="faArrowUpRight"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>
        <campaign-launch-modal
            v-if="props.resource.type === 'campaign' && data"
            v-model:visible="campaignLaunch"
            v-model:campaign="data"
        />
    </div>
</template>

<style scoped>
@reference "@css/style.css";

.resource-dragging .resource-dragging-hide {
    @apply !hidden;
}

.resource-dragging .resource-item {
    transform: rotate(-14deg);
    @apply !w-auto overflow-hidden !border-0;
}

.resource-dragging .resource-icon {
    @apply p-4 !w-auto;
}
</style>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faMessages,
    faToggleLargeOff,
    faToggleLargeOn,
    faMapPin,
    faContactBook,
    faLoader,
    faRobot,
    faEllipsis,
    faArrowUpRight,
    faPlay,
    faCircleMinus,
    faGripDotsVertical,
    faAddressBook,
    faPause,
} from '@fortawesome/pro-solid-svg-icons';
import { computed, onMounted, ref } from 'vue';
import { useCustomFlow } from './useCustomFlow';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxSwal,
    vxFormatCount,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { SegmentTypeToLabel } from '~/constants/segment';
import SegmentRowExport from '~/components/contacts/segments/SegmentRowExport.vue';
import { automationRulesClient } from '~/services';
import CampaignLaunchModal from '~/components/campaigns/CampaignLaunchModal.vue';
import CampaignCardActions from '~/components/campaigns/campaign-card/CampaignCardActions.vue';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { throwSwalRequestError } from '~/utils/request';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import SegmentDescriptionPopover from '~/components/contacts/segments/SegmentDescriptionPopover.vue';
import { CAMPAIGN_STATUSES } from '~/constants/campaign';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
    simple: {
        type: Boolean,
        default: false,
    },
    flowSection: {
        type: Number,
        required: false,
    },
});

const emit = defineEmits(['remove']);

const data = ref();

const customFlow = useCustomFlow();

const options = computed(() => {
    if (props.resource.type === 'automation') {
        return {
            color: 'bg-amber-300',
            icon: faRobot,
            edit: '/automation/event/' + props.resource.id,
        };
    }
    if (props.resource.type === 'campaign') {
        return {
            color: 'bg-sky-300',
            icon: faMessages,
            edit: '/messaging/campaigns/' + props.resource.id,
        };
    }
    if (props.resource.type === 'segment') {
        return {
            color: 'bg-violet-300',
            icon: faContactBook,
            edit: '/contacts/segments/' + props.resource.id,
        };
    }

    return {};
});

const publishedAutomatedActions = computed(
    () =>
        data.value.automated_actions?.filter((action) => action.status === 'published')?.length ||
        data.value.live_actions_count ||
        0
);

const totalAutomatedActions = computed(() => data.value.automated_actions?.length || data.value.actions_count || 0);

const { contactsCount: segmentContactsCount } = useSegmentsContactsCount();

onMounted(async () => {
    try {
        data.value = await customFlow.fetchResource(props.resource.type, props.resource.id);
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
        data.value = undefined;
    }
});

const dragStart = () => {
    if (props.simple) {
        return;
    }
    customFlow.resourceDragging.resource = props.resource;
    customFlow.resourceDragging.oldSectionIndex = props.flowSection;
};

const dragEnd = () => {
    if (props.simple) {
        return;
    }
    setTimeout(() => {
        customFlow.resourceDragging.resource = undefined;
        customFlow.resourceDragging.oldSectionIndex = undefined;
        customFlow.resourceDragging.newSectionIndex = undefined;
    }, 50); // this is so CustomFlowSection@dragEnd has some time to pickup the data before cleaning the dragging data.
};

const campaignLaunch = ref(false);

const updateActionsStatus = (status) => {
    VxSwal.confirmation(
        `Update Actions Status`,
        `Are you sure you want to turn ${status === 'published' ? 'on' : 'off'} all actions?`,
        'success',
        () =>
            automationRulesClient
                .updateActionsStatus(teamId, props.resource.id, status)
                .then(() => {
                    if (data.value.automated_actions?.length) {
                        data.value.automated_actions.forEach((action) => {
                            action.status = status;
                        });
                    } else {
                        data.value.live_actions_count = status === 'draft' ? 0 : data.value.actions_count;
                    }
                })
                .catch(throwSwalRequestError)
    );
};
</script>
